import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronDown, Loader2, Save } from "lucide-react";
import { getVzQuotas, saveVzQuotas } from "@/lib/verizonSalesApi";
import { QUOTA_CATEGORIES } from "@/lib/vzQuotas";
import { OverridesEditor } from "./QuotaRuleRow";
import { AddCustomKpi, RemoveCustomBtn, customCat } from "./CustomKpis";

const ORDER = ["activations", "upgrades", "edge", "byod", "tablets", "fwa", "twp_plus", "twp_2mr", "twp_3mr", "autopay", "topups",
  "mr2", "mr3", "mr4", "mr5", "mr6", "mr7", "rewards", "zulu", "lifeline", "csat"];
const ORDERED = [...QUOTA_CATEGORIES].sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));
const sel = "h-8 rounded-md border border-slate-200 bg-white px-2 text-xs";

const TypeSelect = ({ cat, rule, onChange }) => (
  <select value={rule.mode} onChange={(e) => onChange({ mode: e.target.value, value: e.target.value === "source" ? null : rule.value ?? 0 })}
    className={sel} data-testid="vz-quota-bar-type">
    <option value="percent">{cat.kind === "rate" ? "Target %" : `% of ${cat.pctBase}`}</option>
    {cat.kind === "count" && <option value="number">Count per store</option>}
    {cat.source && <option value="source">From file: {cat.source}</option>}
  </select>
);

// Compact quota editor for the Store Goals page: pick a category, enter a number, choose % or count.
export const QuotaSettingsPanel = ({ stores = [], onSaved }) => {
  const [settings, setSettings] = useState(null);
  const [key, setKey] = useState("edge");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getVzQuotas().then(setSettings).catch(() => toast.error("Failed to load quotas")); }, []);
  if (!settings) return <div className="px-4 pb-3"><Loader2 className="w-4 h-4 animate-spin text-teal" /></div>;

  const customs = Object.entries(settings).filter(([, r]) => r.custom);
  const cats = [...ORDERED, ...customs.map(([k, r]) => customCat(k, r))];
  const cat = cats.find((c) => c.key === key) || cats[0];
  const rule = settings[cat.key] || { mode: "percent", value: 0 };
  const update = (patch) => setSettings((s) => ({ ...s, [cat.key]: { ...rule, ...patch } }));
  const save = async () => {
    setSaving(true);
    try { setSettings(await saveVzQuotas(settings)); toast.success("Quota settings saved"); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const removeCustom = (k) => { setSettings((s) => { const n = { ...s }; delete n[k]; return n; }); setKey("edge"); };

  return (
    <div className="px-4 pb-3 space-y-2" data-testid="vz-quota-settings">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500">Quota for</span>
        <select value={cat.key} onChange={(e) => setKey(e.target.value)} className={sel} data-testid="vz-quota-bar-category">
          {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <span className="text-xs text-slate-500">=</span>
        {rule.mode !== "source" && <Input type="number" min={0} step="any" value={rule.value ?? ""} onChange={(e) => update({ value: e.target.value })}
          className="h-8 w-20 text-xs" data-testid="vz-quota-bar-value" />}
        <TypeSelect cat={cat} rule={rule} onChange={update} />
        <Button size="sm" onClick={save} disabled={saving} className="bg-teal hover:bg-teal/90" data-testid="vz-quota-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save quotas
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid="vz-quota-bar-more">
          Store overrides ({Object.keys(rule.overrides || {}).length}) & custom KPIs <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>
      </div>
      {open && (
        <div className="space-y-2" data-testid="vz-quota-bar-panel">
          <p className="text-xs text-slate-500">Store overrides for <b>{cat.label}</b> (click Save quotas after changes):</p>
          <OverridesEditor cat={cat} rule={rule} stores={stores} onChange={update} />
          {cat.custom && <RemoveCustomBtn k={cat.key} onRemove={removeCustom} />}
          <AddCustomKpi settings={settings} onAdd={(k, r) => { setSettings((s) => ({ ...s, [k]: r })); setKey(k); }} />
        </div>
      )}
    </div>
  );
};
